import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Button } from 'reactstrap';
import { actions } from '../../../context';
import BtxSwitch from '../../Forms/BtxSwitch';
import ConfigAttribute from '../Shared/ConfigAttribute';
import Lock from '../Shared/Lock';
import NewConfiguration from '../Shared/NewConfiguration';
import PimsConfiguration from './PimsConfiguration';

const Pims = () => {
    const dispatch = useDispatch();
    const params = useParams();
    const [pims, setPims] = useState({});
    const [locked, setLocked] = useState(true);
    const [configFilter, setConfigFilter] = useState('');
    const [newConfigurationOpen, setNewConfigurationOpen] = useState(false);
    const [apps, setApps] = useState([]);
    const [selectedApp, setSelectedApp] = useState('')

    const toggleNewConfiguration = useCallback(
        () => setNewConfigurationOpen(!newConfigurationOpen),
        [newConfigurationOpen]
    );

    let pimsId = params.pimsId;
    const refresh = useCallback(
        () => dispatch(actions.pims.fetchPims(pimsId)).then((res) => { 
            setPims(res);
            setSelectedApp(res.appId);
           }
        ),
        [pimsId, dispatch]
    );

    const toggleConnectivity = useCallback(
        async () =>
            dispatch(
                actions.pims.togglePimsSupportsConnectivity(params.pimsId)
            ).then(async () => await refresh()),
        [params.pimsId, dispatch, refresh]
    );

    const toggleLocked = useCallback(() => setLocked(!locked), [locked]);

    const createNewPimsConfiguration = useCallback(
        async (vals) => {
            const payload = { ...vals, entityId: pims.id };
            dispatch(actions.pims.createPimsConfig(payload)).finally(
                async () => await refresh()
            );
        },
        [dispatch, refresh, pims.id]
    );

    const pimsConfigFilter = (c) =>
        configFilter.length === 0 ||
        c.key.toLowerCase().includes(configFilter.toLowerCase());

    const filteredPimsConfigurations = (pims.configurations || []).filter(
        pimsConfigFilter
    );

    const toggleSelectedApp = useCallback(
        async (app) => {
            const payload = { id: pims.id, appId:app };
            dispatch(actions.pims.updatePimsAppRelation(payload)).finally(
                async () => await refresh()
            );
        },
        [dispatch, refresh, pims.id]
    );

    useEffect(() => {
        refresh().then(() => setLocked(true));
        // eslint-disable-next-line
        dispatch(actions.apps.fetchApps()).then((res) => {
            setApps(res);
        });
    }, [dispatch, params.pimsId]);

    return (
        <>
            <NewConfiguration
                configFor={pims.name}
                isOpen={newConfigurationOpen}
                toggle={toggleNewConfiguration}
                submit={createNewPimsConfiguration}
            />
            <div
                style={{
                    width: '100% !important',
                }}>
                <div className="d-flex justify-content-between unselectable">
                    <Link to="/admin/pims" style={{ textDecoration: 'none' }}>
                        <Button color="danger">
                            <span className="text-nowrap">
                                <FontAwesomeIcon icon={faTimes} />
                                &emsp;Close
                            </span>
                        </Button>
                    </Link>
                    <h1>{pims.name || 'Loading'}</h1>
                    <Lock isLocked={locked} onClick={toggleLocked} size="2x" />
                </div>
                <div
                    style={{
                        maxHeight: '80vh',
                        overflowY: 'scroll',
                        overflowX: 'hidden',
                    }}>
                    <ConfigAttribute>
                        Active practices:&emsp;{pims.activePracticeCount || 0}
                    </ConfigAttribute>
                    <ConfigAttribute>
                        <div className="d-flex">
                            <span className="text-nowrap pt-2">
                                Supports connectivity:
                            </span>
                            &emsp;
                            <BtxSwitch
                                disabled={locked}
                                value={pims.supportsConnectivity}
                                onChange={toggleConnectivity}
                            />
                        </div>
                    </ConfigAttribute>
                    <ConfigAttribute>
                        App
                        &emsp;
                        <select
                            disabled={locked}
                            id="appid-select"
                            className="rounded p-1"
                            defaultValue={pims.appId}
                            value={selectedApp}
                            onChange={(e) => {
                                setSelectedApp(e.target.value);
                                toggleSelectedApp(e.target.value)
                            }}
                        >
                            {apps?.map((a, x) => (
                                <option key={x} value={a.id}>
                                    {a.name}
                                </option>
                            ))}
                        </select>
                    </ConfigAttribute>
                    <ConfigAttribute>
                        Configurations ({(pims.configurations || []).length}):
                    </ConfigAttribute>
                    <ConfigAttribute>
                        <div className="d-flex justify-content-between">
                            <input
                                type="search"
                                className="rounded w-25 p-2 text-14"
                                placeholder="Search Configurations"
                                onChange={(e) =>
                                    setConfigFilter(e.target.value)
                                }
                            />
                            <Button
                                className={classNames({ 'd-none': locked })}
                                color="primary"
                                onClick={toggleNewConfiguration}>
                                <FontAwesomeIcon icon={faPlus} /> New Config
                            </Button>
                        </div>
                    </ConfigAttribute>
                    <div>
                        <table>
                            <tbody>
                                {filteredPimsConfigurations.map((c) => (
                                    <PimsConfiguration
                                        key={c.id}
                                        config={c}
                                        refresh={refresh}
                                        dispatch={dispatch}
                                        editing={!locked}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Pims;
